import type { RadioGroupProps as PrimitiveRadioGroupProps } from '@connected/ui/components/radio-group';
import type { ReactNode } from 'react';

import type { OptionExtra } from '../../lib/option';
import type { PrimativeFieldProps, PrimativeFieldPropsOmit, PrimativeFormPropsOmit, PrimitiveValueType } from '../../lib/utils';
import type { AnyRadioGroupRenderItem, RadioGroupOption, RadioGroupRenderItem } from '../form/radio-group';

import { useFieldContext } from '../../hooks/use-form-context';
// DefaultRadioGroupItem is imported as a value (not type) because typeof requires the actual value
// This is a false positive from the linter - typeof in type contexts requires the value to be imported
import { RadioGroup as PrimativeRadioGroup, RadioGroupItems, RadioGroupTrigger } from '../form/radio-group';

type InferValueFromOptions<TOptions extends readonly RadioGroupOption<PrimitiveValueType, OptionExtra | undefined>[]> = TOptions[number] extends RadioGroupOption<infer Value, any> ? Value : never;
type InferExtraFromOptions<TOptions extends readonly RadioGroupOption<PrimitiveValueType, OptionExtra | undefined>[]> = TOptions[number] extends RadioGroupOption<any, infer Extra> ? Extra : never;

type NonAsChildRadioGroupProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = AnyRadioGroupRenderItem> = PrimativeFieldProps<Value> & {
    asChild?: false;
    options: RadioGroupOption<Value, Extra>[];
    orientation?: 'horizontal' | 'vertical';
    renderItem?: RenderItem;
    className?: string;
    children?: ReactNode;
} & Omit<PrimitiveRadioGroupProps, PrimativeFormPropsOmit>;

export type RadioGroupProps<TOptions extends readonly RadioGroupOption<PrimitiveValueType, OptionExtra | undefined>[] = RadioGroupOption<PrimitiveValueType, OptionExtra | undefined>[], Value extends PrimitiveValueType = InferValueFromOptions<TOptions>, Extra extends OptionExtra | undefined = InferExtraFromOptions<TOptions>, RenderItem extends RadioGroupRenderItem<Value, Extra> = RadioGroupRenderItem<Value, Extra>> = Omit<NonAsChildRadioGroupProps<Value, Extra, RenderItem>, 'id' | 'name' | 'value' | 'onChange' | 'onBlur'> & {
    options: TOptions;
    renderItem?: RenderItem;
};

export function RadioGroup<TOptions extends readonly RadioGroupOption<PrimitiveValueType, OptionExtra | undefined>[]>(props: RadioGroupProps<TOptions>) {
    type Value = InferValueFromOptions<TOptions>;
    const field = useFieldContext<Value>();

    return (
        <PrimativeRadioGroup
            id={field.name}
            name={field.name}
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            {...props}
        />
    );
}

RadioGroup.displayName = 'RadioGroup';

export type RadioGroupFieldProps<TValue extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<TValue, Extra> = AnyRadioGroupRenderItem> = Omit<NonAsChildRadioGroupProps<TValue, Extra, RenderItem>, PrimativeFieldPropsOmit | 'options'> & {
    options: RadioGroupOption<TValue, Extra>[];
};

RadioGroup.Items = RadioGroupItems;
RadioGroup.Trigger = RadioGroupTrigger;
